import { useState, useEffect, useRef, useCallback } from 'react';
import { listRefreshRuns, startDevRefresh, continueDevRefresh } from '../api/adminApi';
import type { RefreshRun, PhaseCounts } from '../api/adminApi';

const PHASES: Array<{ id: string; label: string; countsKey?: keyof NonNullable<RefreshRun['row_counts']> }> = [
  { id: 'truncate',          label: 'Truncate Dev tables' },
  { id: 'ghost-auth',        label: 'Create ghost users',         countsKey: 'ghost_auth' },
  { id: 'allowlisted-auth',  label: 'Copy allowlisted users',     countsKey: 'allowlisted_auth' },
  { id: 'data',              label: 'Copy table rows' },
  { id: 'storage',           label: 'Copy storage objects',       countsKey: 'storage' },
];

const PHASE_INDEX: Record<string, number> = Object.fromEntries(PHASES.map((p, i) => [p.id, i]));
const ADVANCEABLE = new Set(['ghost-auth', 'allowlisted-auth', 'data', 'storage']);
const POLL_INTERVAL_MS = 2000;

function formatCounts(counts: PhaseCounts | undefined): string {
  if (!counts) return '';
  const parts: string[] = [];
  if (counts.created !== undefined) parts.push(`created ${counts.created}`);
  if (counts.copied  !== undefined) parts.push(`copied ${counts.copied}`);
  if (counts.skipped !== undefined) parts.push(`skipped ${counts.skipped}`);
  if (counts.failed  !== undefined) parts.push(`failed ${counts.failed}`);
  return parts.join(', ');
}

function durationStr(startIso: string, endIso?: string | null): string {
  const start = new Date(startIso).getTime();
  const end = endIso ? new Date(endIso).getTime() : Date.now();
  const sec = Math.max(0, Math.round((end - start) / 1000));
  if (sec < 60) return `${sec}s`;
  return `${Math.floor(sec / 60)}m ${sec % 60}s`;
}

type PhaseRenderState = 'done' | 'running' | 'failed' | 'pending';

function phaseIdx(id: string): number {
  return PHASE_INDEX[id] ?? -1;
}

function phaseRenderState(phaseId: string, run: RefreshRun): PhaseRenderState {
  if (run.status === 'failed') {
    // current_phase points at the phase that failed (we no longer overwrite to 'failed').
    if (phaseId === run.current_phase) return 'failed';
    if (phaseIdx(phaseId) < phaseIdx(run.current_phase)) return 'done';
    return 'pending';
  }
  if (run.current_phase === 'complete') return 'done';
  if (phaseId === run.current_phase) return 'running';
  if (phaseIdx(phaseId) < phaseIdx(run.current_phase)) return 'done';
  return 'pending';
}

export function DevRefresh() {
  const [runs, setRuns] = useState<RefreshRun[]>([]);
  const [active, setActive] = useState<RefreshRun | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [_now, setNow] = useState(Date.now()); // tick to refresh "elapsed" while active
  const autoAdvanceLockRef = useRef<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const { rows } = await listRefreshRuns();
      setRuns(rows);
      setActive(rows.find(r => r.status === 'running') ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load refresh runs');
    }
  }, []);

  // Polling tick: refresh state + bump `_now` so the elapsed timer updates.
  useEffect(() => {
    refresh();
    const t = setInterval(() => {
      setNow(Date.now());
      refresh();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [refresh]);

  // Auto-advance: when there's an active run whose current_phase is advanceable,
  // chain continueDevRefresh calls until the run is terminal. One loop per run id.
  useEffect(() => {
    if (!active) return;
    if (active.status !== 'running') return;
    if (!ADVANCEABLE.has(active.current_phase)) return;
    if (autoAdvanceLockRef.current === active.id) return;
    autoAdvanceLockRef.current = active.id;
    let cancelled = false;

    (async () => {
      try {
        while (!cancelled) {
          const { rows } = await listRefreshRuns();
          const a = rows.find(r => r.id === active.id);
          setRuns(rows);
          setActive(a ?? null);
          if (!a) break;
          if (a.status !== 'running') break;
          if (!ADVANCEABLE.has(a.current_phase)) break;

          try {
            const result = await continueDevRefresh(a.id);
            if (result.status !== 'running') break;
            if (!ADVANCEABLE.has(result.currentPhase)) break;
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Phase advance failed');
            break;
          }
        }
      } finally {
        autoAdvanceLockRef.current = null;
        await refresh();
      }
    })();

    return () => { cancelled = true; };
  }, [active?.id, active?.status, active?.current_phase, refresh]);

  async function handleStartRefresh() {
    if (!confirm('Start a Dev data refresh? This wipes Dev tables and reloads from Prod.')) return;
    setStarting(true);
    setError(null);
    try {
      await startDevRefresh();
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start refresh');
    } finally {
      setStarting(false);
    }
  }

  const finishedRuns = runs.filter(r => r.status !== 'running').slice(0, 20);

  return (
    <div style={{ padding: 16 }}>
      <h1>Refresh Dev Data</h1>

      {error && (
        <div style={{ color: '#c33', background: '#fee', padding: 8, borderRadius: 4, margin: '8px 0' }}>
          {error}
        </div>
      )}

      {active ? (
        <div style={{ marginTop: 8 }}>
          <h2 style={{ marginBottom: 4 }}>Active run</h2>
          <p style={{ color: '#888', fontSize: '0.9em', margin: '0 0 12px' }}>
            Started {new Date(active.started_at).toLocaleString()}
            {' · '}
            {durationStr(active.started_at)} elapsed
          </p>
          <ol style={{ listStyle: 'none', padding: 0, margin: '8px 0 0' }}>
            {PHASES.map(p => {
              const st = phaseRenderState(p.id, active);
              const icon = st === 'done' ? '✓' : st === 'running' ? '⟳' : st === 'failed' ? '✗' : '○';
              const color = st === 'done' ? '#2a8' : st === 'running' ? '#fa3' : st === 'failed' ? '#c33' : '#888';
              const counts = p.countsKey ? formatCounts(active.row_counts?.[p.countsKey]) : '';
              return (
                <li key={p.id} style={{ padding: '4px 0', color, display: 'flex', alignItems: 'center' }}>
                  <span style={{ display: 'inline-block', width: 24, fontWeight: 600 }}>{icon}</span>
                  <span style={{ fontWeight: st === 'running' ? 600 : 400 }}>{p.label}</span>
                  {counts && <span style={{ marginLeft: 12, color: '#888', fontSize: '0.85em' }}>{counts}</span>}
                </li>
              );
            })}
          </ol>
          {active.error_message && (
            <div style={{ color: '#c33', background: '#fee', padding: 8, borderRadius: 4, marginTop: 12 }}>
              {active.error_message}
            </div>
          )}
        </div>
      ) : (
        <button onClick={handleStartRefresh} disabled={starting} style={{ marginTop: 12 }}>
          {starting ? 'Starting…' : 'Refresh Dev Data'}
        </button>
      )}

      <h2 style={{ marginTop: 32 }}>Run history</h2>
      {finishedRuns.length === 0 ? (
        <p style={{ color: '#888' }}>No completed runs yet.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th align="left">Started</th>
              <th align="left">Duration</th>
              <th align="left">Status</th>
              <th align="left">Last phase</th>
              <th align="left">Error</th>
            </tr>
          </thead>
          <tbody>
            {finishedRuns.map(r => (
              <tr key={r.id} style={{ borderTop: '1px solid #eee' }}>
                <td>{new Date(r.started_at).toLocaleString()}</td>
                <td>{r.finished_at ? durationStr(r.started_at, r.finished_at) : '—'}</td>
                <td style={{ color: r.status === 'success' ? '#2a8' : r.status === 'partial' ? '#fa3' : r.status === 'failed' ? '#c33' : '#888' }}>
                  {r.status}
                </td>
                <td>{r.current_phase}</td>
                <td style={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r.error_message ?? ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
